import axios, { AxiosInstance } from "axios";
import { Equipment } from "@/types/equipment";
import { apiConfig, getWsUrl } from "@/config/api";
import { shouldBounceToInternalLogin } from "@/lib/authRedirect";
import { soltarDonoDoPush } from "@/lib/push/donoDoPush";
import { invalidarStatusSeForManutencao } from "@/lib/maintenanceResponseInterceptor";
import { tratar401 } from "@/lib/sessao/tratar401";

// Use centralized API configuration
const API_BASE_URL = apiConfig.baseURL;

// Create axios instance with default config
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add request interceptor to inject auth token and tenant headers
  instance.interceptors.request.use(
    (config) => {
      // Get auth token and extract company slug from JWT
      try {
        // Sem `seweb_auth` é estado NORMAL (páginas do Portal usam `portalApi`;
        // rotas públicas não têm sessão interna) — não logar. Os console.log/warn
        // de debug em toda request foram removidos (poluíam o console — bug
        // reportado 2026-06-25). Mantém-se só console.error de falha real.
        const authData = localStorage.getItem("seweb_auth");
        if (authData) {
          const parsed = JSON.parse(authData);

          // Add Authorization header
          if (parsed.access_token) {
            config.headers.Authorization = `Bearer ${parsed.access_token}`;

            // Extract company_slug from JWT token
            try {
              const tokenPayload = JSON.parse(atob(parsed.access_token.split(".")[1]));
              if (tokenPayload.company_slug) {
                config.headers["X-Company-Slug"] = tokenPayload.company_slug;
              }
            } catch (jwtError) {
              console.error("[API] Failed to decode JWT token:", jwtError);
            }
          }
        }
      } catch (error) {
        console.error("Failed to get auth token:", error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling + OBS-1 error tracking
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;

      if (status === 401) {
        // O 401 tenta renovar a sessão e repetir o pedido UMA vez antes de derrubar a pessoa (`tratar401`). Só o desfecho "sair" faz o
        // que este ramo sempre fez; "esperar" (manutenção, rede) deixa a sessão como está e só rejeita.
        const desfecho = await tratar401(instance, error);
        if (desfecho.tipo === "repetida") return desfecho.resposta;
        if (desfecho.tipo === "sair") {
          // Handle unauthorized - token expired or invalid
          console.error("Unauthorized access - token expired, logging out");
          localStorage.removeItem("seweb_auth");
          // O 401 é o fim da sessão interna: a marca de dono do push do aparelho sai junto (se for do interno), senão o parceiro do portal
          // no mesmo navegador não toma o push e os avisos do colaborador seguem chegando ali (decisão de 12/10/2026).
          soltarDonoDoPush("interno");
          if (shouldBounceToInternalLogin(window.location.pathname)) {
            window.location.href = "/login";
          }
        }
      }

      // Manutenção (28/09/2026, contrato Front — docs/superpowers/plans/
      // 2026-09-28-manutencao-no-login.md): 503 de manutenção pode chegar de
      // QUALQUER chamada (não só login — a carência expira, um módulo entra
      // em manutenção no meio da sessão etc.). Invalidar a query aqui troca a
      // tela NA HORA em vez de esperar até 30s do polling do
      // MaintenanceProvider. Nada de logout — manutenção não é sessão
      // inválida. Função compartilhada com @/lib/apiClient (achado BAIXO,
      // item 10 — a outra instância axios do projeto não tinha isto).
      invalidarStatusSeForManutencao(error);

      // OBS-1: Capture network errors (skip 401 login-expired and 429 rate-limit)
      if (status && status !== 401 && status !== 429 && status >= 400) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports -- lazy require para evitar ciclo de imports
          const { errorTracker } = require("@/services/errorTrackingService");
          const endpoint = error.config?.url || "unknown";
          const responseBody =
            typeof error.response?.data === "string"
              ? error.response.data
              : JSON.stringify(error.response?.data || {});
          errorTracker.captureNetworkError(endpoint, status, responseBody);
        } catch {
          /* tracker not available */
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

const api = createAxiosInstance();

// Equipment API endpoints
export interface EquipmentFilters {
  search?: string;
  type?: string;
  status?: string;
  location?: string;
  limit?: number;
  offset?: number;
}

export interface EquipmentListResponse {
  items: Equipment[];
  total: number;
  limit: number;
  offset: number;
}

export interface EquipmentUpdatePayload {
  name?: string;
  equipment_type?: string;
  status?: string;
  location?: string;
  ip_address?: string;
  mac_address?: string;
  serial_number?: string;
  model?: string;
  manufacturer?: string;
  os_info?: string;
  heartbeat_interval?: number;
  responsible_user_id?: string;
}

export const equipmentApi = {
  /**
   * Get all equipments with optional filters
   * Respects tenant isolation via headers
   */
  getAll: async (filters?: EquipmentFilters): Promise<EquipmentListResponse> => {
    const params: Record<string, string | number> = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.type) params.equipment_type = filters.type;
    if (filters?.status) params.status = filters.status;
    if (filters?.location) params.location = filters.location;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;

    const response = await api.get<EquipmentListResponse>("/equipments", { params });
    return response.data;
  },

  /**
   * Get a single equipment by ID
   */
  getById: async (id: string): Promise<Equipment> => {
    const response = await api.get<Equipment>(`/equipments/${id}`);
    return response.data;
  },

  /**
   * Create a new equipment
   */
  create: async (
    data: Omit<Equipment, "id" | "created_at" | "updated_at" | "company_id">
  ): Promise<Equipment> => {
    const response = await api.post<Equipment>("/equipments", data);
    return response.data;
  },

  /**
   * Update an equipment
   */
  update: async (id: string, data: EquipmentUpdatePayload): Promise<Equipment> => {
    const response = await api.put<Equipment>(`/equipments/${id}`, data);
    return response.data;
  },

  /**
   * Delete an equipment
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/equipments/${id}`);
  },

  /**
   * Batch update equipment status
   */
  batchUpdateStatus: async (ids: string[], status: string): Promise<void> => {
    await api.post("/equipments/batch-update-status", { ids, status });
  },
};

// WebSocket utilities
/**
 * URL completa de um endpoint WebSocket.
 *
 * 🔴 Delega para `getWsUrl` de `@/config/api`. Até 31/08/2026 esta função tinha
 * implementação PRÓPRIA, e o resultado em produção era
 * `wss://ws//98.81.250.213:8080/...` — host `ws`, DNS irresolúvel.
 *
 * A causa: ela montava `` `${protocolo}//${VITE_WS_URL}${path}` ``, tratando
 * `VITE_WS_URL` como host. O valor que o compose passa é
 * `ws://98.81.250.213:8080` — uma URL COM protocolo. Prefixar `wss://` a isso
 * dá um host que não existe.
 *
 * ⚠️ Duas funções com o MESMO nome e contratos diferentes (`config/api.ts` tem
 *    outra `getWebSocketUrl`, que devolve só a base) é o que deixou o erro
 *    invisível por dois dias. Agora há uma implementação, e ela resolve o host
 *    por `window.location` em produção — resiliente a troca de IP da VM.
 *
 * ⚠️ O `path` deve vir COMPLETO, com `/api/v1`: é o único prefixo que o nginx
 *    encaminha como WebSocket (`location ~ ^/api/v1/ws(/|$)`).
 */
export const getWebSocketUrl = (path: string): string => getWsUrl(path);

// Export as both default and named export for compatibility
export default api;
export { api };
